import { useState } from 'react'
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Badge,
    useMediaQuery,
    useTheme,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField
} from '@mui/material'
import prospectEmptyIcon from '../../assets/prospect_empty_icon.svg'
import axiosClient from '../../services/axiosClient'
import { router } from '@inertiajs/react'
import TourRequestCard, { TourRequest } from './TourRequestCard'

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tours-tabpanel-${index}`}
            aria-labelledby={`tours-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 0 }}>{children}</Box>}
        </div>
    )
}

interface ToursTabProps {
    pending_not_expired?: TourRequest[]
    pending_expired?: TourRequest[]
    accepted_past?: TourRequest[]
    accepted_upcoming?: TourRequest[]
    initialTab?: number
}

export default function ToursTab({
    pending_not_expired = [],
    pending_expired = [],
    accepted_past = [],
    accepted_upcoming = [],
    initialTab = 0
}: ToursTabProps) {
    const [activeTab, setActiveTab] = useState(initialTab)
    const [processing, setProcessing] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [declineDialogOpen, setDeclineDialogOpen] = useState(false)
    const [selectedTourId, setSelectedTourId] = useState<string | null>(null)
    const [declineReason, setDeclineReason] = useState<string>('')
    const [customReason, setCustomReason] = useState<string>('')

    const theme = useTheme()
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

    // Combine pending requests for the Requests tab
    const allPendingRequests = [...pending_not_expired, ...pending_expired]
    const pendingCount = pending_not_expired.length
    const scheduledCount = accepted_upcoming.length
    const touredCount = accepted_past.length

    const handleAcceptTour = async (tourId: string) => {
        try {
            setProcessing(tourId)
            const response = await axiosClient.patch(`/tour_requests/${tourId}/accept`)

            if (response.data.success) {
                // Reload the page to get updated data
                router.reload()
            }
        } catch (err) {
            console.error('Error accepting tour:', err)
            setError('Failed to accept tour request')
        } finally {
            setProcessing(null)
        }
    }

    const handleOpenDeclineDialog = (tourId: string) => {
        setSelectedTourId(tourId)
        setDeclineReason('')
        setCustomReason('')
        setDeclineDialogOpen(true)
    }

    const handleCloseDeclineDialog = () => {
        setDeclineDialogOpen(false)
        setSelectedTourId(null)
        setDeclineReason('')
        setCustomReason('')
    }

    const handleConfirmDecline = async () => {
        if (!selectedTourId) return

        // Validate that a reason is selected
        if (!declineReason) {
            setError('Please select a reason for declining')
            return
        }

        // If "Other" is selected, validate custom reason
        if (declineReason === 'other' && !customReason.trim()) {
            setError('Please provide a reason')
            return
        }

        const finalReason = declineReason === 'other' ? customReason : declineReason

        try {
            setProcessing(selectedTourId)
            const response = await axiosClient.patch(`/tour_requests/${selectedTourId}/decline`, {
                rejection_reason: finalReason
            })

            if (response.data.success) {
                handleCloseDeclineDialog()
                // Reload the page to get updated data
                router.reload()
            }
        } catch (err) {
            console.error('Error declining tour:', err)
            setError('Failed to decline tour request')
        } finally {
            setProcessing(null)
        }
    }

    const handleCancelTour = async (tourId: string) => {
        try {
            setProcessing(tourId)
            const response = await axiosClient.patch(`/tour_requests/${tourId}/decline`, {
                rejection_reason: 'Cancelled by prospect'
            })

            if (response.data.success) {
                // Reload the page to get updated data
                router.reload()
            }
        } catch (err) {
            console.error('Error cancelling tour:', err)
            setError('Failed to cancel tour')
        } finally {
            setProcessing(null)
        }
    }

    return (
        <Box sx={{ width: '100%', maxWidth: isDesktop ? 750 : 560, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            {error && (
                <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                aria-label="tours sub-tabs"
                textColor="inherit"
                TabIndicatorProps={{ style: { background: '#171717', height: 2 } }}
                sx={{ mb: 2, width: '100%', maxWidth: isDesktop ? 650 : 560 }}
            >
                <Tab
                    label={
                        <Badge
                            badgeContent={pendingCount}
                            color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    right: -10,
                                    top: 3
                                }
                            }}
                        >
                            <span style={{ fontWeight: activeTab === 0 ? 700 : 500, color: '#171717' }}>
                                Requests
                            </span>
                        </Badge>
                    }
                    sx={{ minWidth: 0, px: isDesktop ? 4 : 2.5, textTransform: 'none', fontSize: 17 }}
                />
                <Tab
                    label={
                        <Badge
                            badgeContent={scheduledCount}
                            sx={{
                                '& .MuiBadge-badge': {
                                    right: -10,
                                    top: 3,
                                    backgroundColor: '#228B22',
                                    color: 'white'
                                }
                            }}
                        >
                            <span style={{ fontWeight: activeTab === 1 ? 700 : 500, color: '#171717' }}>
                                Scheduled
                            </span>
                        </Badge>
                    }
                    sx={{ minWidth: 0, px: isDesktop ? 4 : 2.5, textTransform: 'none', fontSize: 17 }}
                />
                <Tab
                    label={
                        <Badge
                            badgeContent={touredCount}
                            sx={{
                                '& .MuiBadge-badge': {
                                    right: -10,
                                    top: 3,
                                    backgroundColor: '#171717',
                                    color: 'white'
                                }
                            }}
                        >
                            <span style={{ fontWeight: activeTab === 2 ? 700 : 500, color: '#171717' }}>
                                Toured
                            </span>
                        </Badge>
                    }
                    sx={{ minWidth: 0, px: isDesktop ? 4 : 2.5, textTransform: 'none', fontSize: 17 }}
                />
            </Tabs>

            {/* Tour Requests Tab */}
            <TabPanel value={activeTab} index={0}>
                {allPendingRequests.length === 0 ? (
                    <Box sx={{ width: '100%', maxWidth: isDesktop ? 750 : 560, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, textAlign: 'left', ml: 0 }}>
                            Tour requests from landlords will appear here.
                        </Typography>
                        <Typography sx={{ color: '#222', ml: 0, fontSize: 16, mb: 4, textAlign: 'left', maxWidth: 400, mt: 0 }}>
                            Everything in one place.
                        </Typography>
                        <img src={prospectEmptyIcon} alt="No Tour Requests" style={{ width: isDesktop ? 180 : 120, margin: '0 auto' }} />
                    </Box>
                ) : (
                    <Box sx={{ width: '100%', maxWidth: isDesktop ? 750 : 560, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', mt: 0 }}>
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {allPendingRequests.map((tour) => {
                                    const isExpired = pending_expired.some(expired => expired.id === tour.id)
                                    return (
                                        <TourRequestCard
                                            key={tour.id}
                                            tour={tour}
                                            isExpired={isExpired}
                                            processing={processing}
                                            variant="pending"
                                            onAcceptTour={handleAcceptTour}
                                            onDeclineTour={handleOpenDeclineDialog}
                                        />
                                    )
                                })}
                            </Box>
                        </Box>
                    </Box>
                )}
            </TabPanel>

            {/* Scheduled Tours Tab */}
            <TabPanel value={activeTab} index={1}>
                {accepted_upcoming.length === 0 ? (
                    <Box sx={{ width: '100%', maxWidth: isDesktop ? 750 : 560, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, textAlign: 'left', ml: 0 }}>
                            Scheduled tours will appear here.
                        </Typography>
                        <Typography sx={{ color: '#222', ml: 0, fontSize: 16, mb: 4, textAlign: 'left', maxWidth: 400, mt: 0 }}>
                            Everything in one place.
                        </Typography>
                        <img src={prospectEmptyIcon} alt="No Scheduled Tours" style={{ width: isDesktop ? 180 : 120, margin: '0 auto' }} />
                    </Box>
                ) : (
                    <Box sx={{ width: '100%', maxWidth: isDesktop ? 750 : 560, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', mt: 0 }}>
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {accepted_upcoming.map((tour) => (
                                    <TourRequestCard
                                        key={tour.id}
                                        tour={tour}
                                        processing={processing}
                                        variant="scheduled"
                                        onCancelTour={handleCancelTour}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}
            </TabPanel>

            {/* Toured Tab */}
            <TabPanel value={activeTab} index={2}>
                {accepted_past.length === 0 ? (
                    <Box sx={{ width: '100%', maxWidth: isDesktop ? 750 : 560, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, textAlign: 'left', ml: 0 }}>
                            Past tours will appear here.
                        </Typography>
                        <Typography sx={{ color: '#222', ml: 0, fontSize: 16, mb: 4, textAlign: 'left', maxWidth: 400, mt: 0 }}>
                            Everything in one place.
                        </Typography>
                        <img src={prospectEmptyIcon} alt="No Past Tours" style={{ width: isDesktop ? 180 : 120, margin: '0 auto' }} />
                    </Box>
                ) : (
                    <Box sx={{ width: '100%', maxWidth: isDesktop ? 750 : 560, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', mt: 0 }}>
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {accepted_past.map((tour) => (
                                    <TourRequestCard
                                        key={tour.id}
                                        tour={tour}
                                        processing={processing}
                                        variant="past"
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}
            </TabPanel>

            {/* Decline Reason Dialog */}
            <Dialog
                open={declineDialogOpen}
                onClose={handleCloseDeclineDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '16px', p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
                    Why are you declining this tour request?
                </DialogTitle>
                <DialogContent>
                    <RadioGroup
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                    >
                        <FormControlLabel
                            value="Listing missing key features"
                            control={<Radio sx={{ color: 'grey.500', '&.Mui-checked': { color: 'black' } }} />}
                            label="Listing missing key features"
                            sx={{ mb: 1 }}
                        />
                        <FormControlLabel
                            value="Photos did not look good"
                            control={<Radio sx={{ color: 'grey.500', '&.Mui-checked': { color: 'black' } }} />}
                            label="Photos did not look good"
                            sx={{ mb: 1 }}
                        />
                        <FormControlLabel
                            value="I did not like the location"
                            control={<Radio sx={{ color: 'grey.500', '&.Mui-checked': { color: 'black' } }} />}
                            label="I did not like the location"
                            sx={{ mb: 1 }}
                        />
                        <FormControlLabel
                            value="The rent is too high"
                            control={<Radio sx={{ color: 'grey.500', '&.Mui-checked': { color: 'black' } }} />}
                            label="The rent is too high"
                            sx={{ mb: 1 }}
                        />
                        <FormControlLabel
                            value="Found something I liked more"
                            control={<Radio sx={{ color: 'grey.500', '&.Mui-checked': { color: 'black' } }} />}
                            label="Found something I liked more"
                            sx={{ mb: 1 }}
                        />
                        <FormControlLabel
                            value="other"
                            control={<Radio sx={{ color: 'grey.500', '&.Mui-checked': { color: 'black' } }} />}
                            label="Other"
                            sx={{ mb: 1 }}
                        />
                    </RadioGroup>

                    {declineReason === 'other' && (
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Please provide a reason..."
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                            sx={{ mt: 2 }}
                            variant="outlined"
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseDeclineDialog}
                        sx={{
                            color: 'grey.700',
                            textTransform: 'none',
                            fontWeight: 500
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDecline}
                        disabled={!declineReason || (declineReason === 'other' && !customReason.trim())}
                        variant="contained"
                        sx={{
                            bgcolor: 'black',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: 'grey.900'
                            },
                            '&:disabled': {
                                bgcolor: 'grey.300',
                                color: 'grey.500'
                            }
                        }}
                    >
                        Decline Tour
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
